import { t, useLocale } from '../lib/i18n.js';
import { LETTERS, renderMask, type RoundPublic } from '@listen-draw/shared';

interface Props {
  round: RoundPublic | null;
  /** 読み上げ中の選択肢（強調） */
  playing?: number | null;
  /** ヒントを表示してよい選択肢の上限（読み上げ済み）。undefined なら全て */
  hintUpTo?: number;
  /** 公開時: 自分の回答 */
  myLetter?: number | null;
  /** 英文を表示する（Host の先生用トグル、または公開後） */
  showTexts?: boolean;
  compact?: boolean;
  /** 参加者画面: 押すと回答が確定する */
  onPick?: (letterIndex: number) => void;
  /** onPick を有効にするか（描画中かつ未回答） */
  pickable?: boolean;
  /** 公開前に自分が押した選択肢 */
  picked?: number | null;
}

export function Choices({
  round,
  playing,
  hintUpTo,
  myLetter,
  showTexts,
  compact,
  onPick,
  pickable,
  picked,
}: Props) {
  useLocale();
  const reveal = round?.reveal;
  const interactive = !!onPick;
  return (
    <div
      className="choices"
      role={interactive ? 'group' : 'list'}
      aria-label={t('Options A–D', '選択肢 A〜D')}
    >
      {LETTERS.map((L, i) => {
        const mask = round?.masks[i];
        const text = reveal?.texts[i] ?? round?.hostTexts?.[i];
        const isCorrect = reveal?.correctLetterIndex === i;
        const revealed = !!reveal || (!!showTexts && !!text);
        const isPicked = picked === i;
        const cls = ['choice'];
        if (playing === i) cls.push('choice--playing');
        if (reveal && isCorrect) cls.push('choice--correct');
        if (revealed) cls.push('choice--revealed');
        if (interactive) cls.push('choice--pickable');
        if (isPicked && !reveal) cls.push('choice--picked');
        if (picked !== null && picked !== undefined && !isPicked && !reveal)
          cls.push('choice--dim');
        const canHint = hintUpTo === undefined || i <= hintUpTo;
        const body = (
          <>
            <span className="choice__letter">{L}</span>
            <span className="choice__text en" style={compact ? { fontSize: 13 } : undefined}>
              {revealed && text ? (
                text
              ) : mask ? (
                renderMask(canHint ? mask : { ...mask, hint: undefined }).map((part, k) => (
                  <span key={k} className={part.isHint ? 'hint' : 'dots'}>
                    {part.text}{' '}
                  </span>
                ))
              ) : (
                <span className="dots">●●●●●●●●●●</span>
              )}
            </span>
            <span
              className={`choice__mark${reveal && myLetter === i && !isCorrect ? ' choice__mark--wrong' : ''}${isPicked && !reveal ? ' choice__mark--mine' : ''}`}
            >
              {reveal && isCorrect
                ? '✓'
                : reveal && myLetter === i
                  ? '✗'
                  : isPicked
                    ? t('Locked', '回答')
                    : ''}
            </span>
          </>
        );
        if (interactive) {
          return (
            <button
              key={L}
              type="button"
              className={cls.join(' ')}
              data-letter={L}
              disabled={!pickable}
              aria-pressed={isPicked}
              aria-label={t(`Choose ${L}`, `${L} を選ぶ`)}
              onClick={() => onPick?.(i)}
            >
              {body}
            </button>
          );
        }
        return (
          <div key={L} className={cls.join(' ')} role="listitem">
            {body}
          </div>
        );
      })}
    </div>
  );
}
